/**
 * @fileoverview ExperienceModal.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import DynamicImage from '../ui/DynamicImage'

function ExperienceModal({ experience, onClose }) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (experience) {
      document.body.style.overflow = 'hidden'
      window.__lenis?.stop()
    } else {
      document.body.style.overflow = 'unset'
      window.__lenis?.start()
    }
    return () => { 
      document.body.style.overflow = 'unset'
      window.__lenis?.start()
    }
  }, [experience])

  if (!experience) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 md:px-0">
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        ></motion.div>

        <motion.div
          className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl z-10 flex flex-col md:flex-row overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/80 backdrop-blur-md rounded-full shadow-md flex items-center justify-center text-gray-800 hover:bg-KITH-primary hover:text-white transition-colors"
          >
            <i className="fi fi-br-cross text-xs"></i>
          </button>

          {/* Image Section */}
          <div className="w-full md:w-[45%] h-64 md:h-auto relative shrink-0">
            <DynamicImage 
              LowImage={experience.image.l} 
              HighImage={experience.image.h} 
              alt={experience.name} 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content Section */}
          <div className="w-full md:w-[55%] p-8 md:p-10 flex flex-col gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight mb-4">
                {experience.name}
              </h2>
              <div className="flex flex-wrap gap-2">
                {experience.categories?.map((cat, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-[10px] md:text-xs font-bold text-KITH-primary bg-KITH-primary/10 rounded-md uppercase tracking-wider"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>

            <div className="w-full h-px bg-gray-100"></div>

            <div className="prose prose-sm md:prose-base prose-gray max-w-none text-gray-600 text-justify leading-relaxed">
              {experience.description?.split('\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default ExperienceModal
